import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Play, Pause, Square, RefreshCw, Zap, Clock, CheckCircle, XCircle, AlertTriangle, ExternalLink, ChevronDown, Trash2, Bot, Cpu } from 'lucide-react';
import { useAdminT } from '@/lib/adminTranslations';

const API_BASE = '';
const LANGS = ['all', 'en', 'tr', 'es', 'pt', 'de', 'fr', 'it', 'nl', 'ru', 'uk', 'ar', 'id'];
const JOB_TYPES = [
  { id: 'match', label: 'Match Content', color: 'bg-emerald-600' },
  { id: 'league', label: 'League Content', color: 'bg-blue-600' },
  { id: 'bookmaker', label: 'Bookmaker Content', color: 'bg-cyan-600' },
  { id: 'prediction', label: 'Predictions', color: 'bg-violet-600' },
  { id: 'faq', label: 'FAQs', color: 'bg-pink-600' },
  { id: 'insight', label: 'Insights', color: 'bg-indigo-600' },
  { id: 'static_pages', label: 'Static Pages', color: 'bg-amber-600' },
];

export default function AdminJobManager() {
  const t = useAdminT();
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [jobs, setJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [models, setModels] = useState([]);
  const [lang, setLang] = useState('all');
  const [model, setModel] = useState('primary');
  const [starting, setStarting] = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [jobRes, logRes, modelRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/jobs`, { headers }),
        fetch(`${API_BASE}/api/admin/jobs/recent-logs?limit=30`, { headers }),
        fetch(`${API_BASE}/api/admin/jobs/models`, { headers }),
      ]);
      setJobs((await jobRes.json()).jobs || []);
      try { setLogs((await logRes.json()).logs || []); } catch {}
      try { setModels((await modelRes.json()).models || []); } catch {}
    } catch {}
  }, [token]);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 5000); return () => clearInterval(i); }, [fetchData]);

  const startJob = async (type) => {
    setStarting(type);
    await fetch(`${API_BASE}/api/admin/jobs/start/${type}?language=${lang}&model=${model}&limit=500`, { method: 'POST', headers });
    await fetchData();
    setStarting(null);
  };

  const jobAction = async (jobId, action) => {
    await fetch(`${API_BASE}/api/admin/jobs/${jobId}/${action}`, { method: 'POST', headers });
    await fetchData();
  };

  const statusCfg = {
    running: { color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: Play },
    paused: { color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Pause },
    completed: { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: CheckCircle },
    cancelled: { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: Square },
  };

  return (
    <div className="space-y-6" data-testid="admin-job-manager">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100" style={{ fontFamily: 'var(--font-heading)' }}>
          <Zap className="w-6 h-6 inline mr-2 text-amber-400" /> AI Job Manager
        </h1>
        <div className="flex gap-2">
          {jobs.some(j => ['completed','cancelled'].includes(j.status)) && (
            <Button size="sm" onClick={async () => { await fetch(`${API_BASE}/api/admin/jobs/clear-completed`, { method: 'POST', headers }); fetchData(); }}
              variant="ghost" className="text-slate-500 hover:text-red-400">
              <Trash2 className="w-3 h-3 mr-1" /> Clear Done
            </Button>
          )}
          <Button size="sm" onClick={fetchData} className="bg-slate-800 text-slate-300 hover:bg-slate-700">
            <RefreshCw className="w-4 h-4 mr-1" /> {t.refresh}
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-500">Auto-refresh 5s | {models.length} AI model connected</p>

      {/* Start New Job */}
      <div className="bg-slate-900/50 border border-emerald-500/10 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-emerald-400 mb-4 uppercase tracking-wider">New AI Job</h2>
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <div>
            <label className="text-[10px] text-slate-500 uppercase block mb-1">Language</label>
            <select value={lang} onChange={e => setLang(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-300 min-w-[100px]" data-testid="job-lang">
              {LANGS.map(l => <option key={l} value={l}>{l === 'all' ? 'ALL LANGS' : l.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase block mb-1">AI Model</label>
            <select value={model} onChange={e => setModel(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-300 min-w-[180px]" data-testid="job-model">
              {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              {models.length === 0 && <option value="primary">primary</option>}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {JOB_TYPES.map(jt => (
            <Button key={jt.id} size="sm" onClick={() => startJob(jt.id)} disabled={starting === jt.id}
              className={`${jt.color} hover:opacity-90 text-white text-xs`} data-testid={`start-${jt.id}`}>
              <Play className="w-3 h-3 mr-1 shrink-0" />
              {starting === jt.id ? '...' : jt.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Active + Recent Jobs */}
      {jobs.length === 0 ? (
        <div className="text-center text-slate-500 py-8">No jobs. Start one above.</div>
      ) : (
        <div className="space-y-3">
          {jobs.map(job => {
            const cfg = statusCfg[job.status] || statusCfg.completed;
            const Icon = cfg.icon;
            const processed = job.completed + job.failed + job.skipped;
            const progress = job.total > 0 ? Math.round((processed / job.total) * 100) : 0;
            const isExpanded = expandedJob === job.job_id;

            return (
              <div key={job.job_id} className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden" data-testid={`job-${job.job_id}`}>
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={cfg.color}><Icon className="w-3 h-3 mr-1" />{job.status}</Badge>
                      <span className="text-sm font-semibold text-slate-200">{job.job_type.replace('_',' ')}</span>
                      <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px]">{job.language?.toUpperCase()}</Badge>
                      <Badge variant="outline" className="border-violet-500/30 text-violet-400 text-[10px]">
                        <Cpu className="w-2.5 h-2.5 mr-0.5" />{job.model}
                      </Badge>
                    </div>
                    <div className="flex gap-1">
                      {job.status === 'running' && (
                        <Button size="sm" variant="ghost" onClick={() => jobAction(job.job_id, 'pause')}
                          className="text-amber-400 hover:bg-amber-500/10 h-7 px-2"><Pause className="w-4 h-4" /></Button>
                      )}
                      {job.status === 'paused' && (
                        <Button size="sm" variant="ghost" onClick={() => jobAction(job.job_id, 'resume')}
                          className="text-emerald-400 hover:bg-emerald-500/10 h-7 px-2"><Play className="w-4 h-4" /></Button>
                      )}
                      {['running','paused'].includes(job.status) && (
                        <Button size="sm" variant="ghost" onClick={() => jobAction(job.job_id, 'cancel')}
                          className="text-red-400 hover:bg-red-500/10 h-7 px-2"><Square className="w-4 h-4" /></Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setExpandedJob(isExpanded ? null : job.job_id)}
                        className="text-slate-400 h-7 px-2">
                        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </Button>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="mb-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-bold text-slate-200">{progress}%</span>
                      <span className="text-slate-500">{processed} / {job.total}</span>
                    </div>
                    <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden flex">
                      <div className="bg-emerald-500 h-full transition-all" style={{ width: `${job.total > 0 ? (job.completed/job.total*100) : 0}%` }} />
                      <div className="bg-red-500 h-full transition-all" style={{ width: `${job.total > 0 ? (job.failed/job.total*100) : 0}%` }} />
                      <div className="bg-slate-600 h-full transition-all" style={{ width: `${job.total > 0 ? (job.skipped/job.total*100) : 0}%` }} />
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex gap-4 text-xs flex-wrap">
                    <span className="text-emerald-400"><CheckCircle className="w-3 h-3 inline mr-0.5" />{job.completed} done</span>
                    <span className="text-red-400"><XCircle className="w-3 h-3 inline mr-0.5" />{job.failed} fail</span>
                    <span className="text-slate-500"><Clock className="w-3 h-3 inline mr-0.5" />{job.skipped} skip</span>
                    <span className="text-amber-400"><AlertTriangle className="w-3 h-3 inline mr-0.5" />{job.remaining} left</span>
                    <span className="text-slate-600">Started: {new Date(job.started_at).toLocaleTimeString()}</span>
                  </div>

                  {/* Current Item */}
                  {job.current_item && job.status === 'running' && (
                    <div className="mt-2 text-xs text-cyan-400 bg-cyan-500/5 border border-cyan-500/10 rounded px-2 py-1.5">
                      <Bot className="w-3 h-3 inline mr-1" /> Working: <strong>{job.current_item}</strong>
                    </div>
                  )}
                </div>

                {/* Expanded: Completed items */}
                {isExpanded && (
                  <div className="border-t border-slate-800 bg-slate-950/50 p-4 max-h-72 overflow-y-auto">
                    <h4 className="text-xs font-semibold text-slate-400 mb-2">Completed Pages ({job.completed_items?.length || 0})</h4>
                    {job.completed_items?.length > 0 ? (
                      <div className="space-y-1">
                        {job.completed_items.map((item, i) => (
                          <div key={i} className="flex items-center justify-between text-xs py-1 px-2 rounded hover:bg-slate-800/50">
                            <div className="flex items-center gap-2">
                              {item.success ? <CheckCircle className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
                              <span className="text-slate-300">{item.slug}</span>
                              <Badge variant="outline" className="border-slate-700 text-slate-500 text-[9px]">{item.language}</Badge>
                              {item.words > 0 && <span className="text-slate-600">{item.words}w</span>}
                              <span className="text-slate-700">{new Date(item.time).toLocaleTimeString()}</span>
                            </div>
                            {item.url && item.success && (
                              <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300">
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-slate-600">No items yet</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Recent AI Logs */}
      {logs.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-3">Recent AI Logs ({logs.length})</h3>
          <div className="space-y-1 max-h-52 overflow-y-auto">
            {logs.map((log, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-1 px-2 rounded hover:bg-slate-800/50">
                <div className="flex items-center gap-2">
                  {log.success ? <CheckCircle className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
                  <span className="text-slate-400">{log.page_type}</span>
                  <span className="text-slate-300 truncate max-w-[180px]">{log.page_key}</span>
                  <Badge variant="outline" className="border-slate-700 text-slate-500 text-[9px]">{log.language}</Badge>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  {log.tokens_used > 0 && <span>{log.tokens_used}t</span>}
                  {log.generation_time_ms > 0 && <span>{(log.generation_time_ms/1000).toFixed(1)}s</span>}
                  <span>{new Date(log.timestamp).toLocaleTimeString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
